import { post, upload, get } from '../libs/api';

export const extractErrorMessage = (
  response,
  fallback = 'Submission failed. Please try again.'
) =>
  response?.error ||
  response?.message ||
  response?.msg ||
  response?.data?.error ||
  response?.data?.message ||
  response?.data?.msg ||
  fallback;

const payload = (response, fallback) => {
  if (
    !response ||
    response.error ||
    response.status === 'ERROR' ||
    Number(response.status) >= 400 ||
    (response.code != null && ![0, 200].includes(Number(response.code)))
  ) {
    throw new Error(extractErrorMessage(response, fallback));
  }
  return response.data ?? response;
};

// JWT handling and auth/maintenance redirects remain in the client's transport.
const generateSchedule = async (endpoint, request) => {
  const response = await post(endpoint, request, false);
  if (response?.code !== 0 || !Array.isArray(response.data))
    throw new Error(
      extractErrorMessage(response, 'Unable to compute schedule.')
    );
  return response.data;
};

const searchReferences = async (
  endpoint,
  { query = '', structureVersionId, fieldCode, underlying, quote }
) => {
  const params = new URLSearchParams({ query });
  for (const [key, value] of Object.entries({
    structureVersionId,
    fieldCode,
    underlying,
    quote,
  })) {
    if (value) params.set(key, String(value));
  }
  const options = payload(
    await get(endpoint + '?' + params.toString()),
    'Reference lookup failed.'
  );
  if (!Array.isArray(options))
    throw new Error('Reference lookup returned invalid options.');
  return options;
};

/**
 * Builds the engine services object the generic trade form runs on.
 *
 * `sourceTransactionId` names the CP trade this booking was copied from, when the
 * desk is an agent (or RM) managing a client and chose to copy one. It is null for every other
 * booking, and the booking call then carries no such field at all — the server only re-checks
 * eligibility and links the trade when one is actually present.
 */
export const createClientEngineServices = ({ sourceTransactionId = null } = {}) => ({
  loadSelectOptions: async ({ query, source }) => {
    if (!source || !source.startsWith('/') || source.startsWith('//'))
      throw new Error('Option source must be a client API path.');
    const options = payload(
      await post(source, { query }),
      'Option lookup failed.'
    );
    if (!Array.isArray(options))
      throw new Error('Option lookup returned invalid options.');
    return options.map((row) => ({ label: row.name, value: row.name, ...row }));
  },
  searchSecurities: (request) =>
    searchReferences('/api/security-master-select/options', request),
  searchIndexes: (request) =>
    searchReferences('/api/index-select/options', request),
  generateFixedAnnuity: (request) =>
    generateSchedule('/api/product/annuity/fixed/generate', request),
  generateDates: (request) =>
    generateSchedule('/api/product/schedule/generate-dates', request),
  uploadFile: async (file) => {
    const response = await upload('/wallet/file/upload', file);
    const data = payload(response, 'File upload failed.');
    if (response.status !== 200 || !data.file_id)
      throw new Error(extractErrorMessage(response, 'File upload failed.'));
    return { fileId: data.file_id };
  },
  evaluateDependencies: async (request) =>
    payload(
      await post(
        '/api/product/structure/evaluate-dependencies',
        request,
        false
      ),
      'Evaluation failed.'
    ),
  loadWalletBalance: async (currency) =>
    payload(
      await post('/wallet/balance', { currency }, false),
      'Wallet balance unavailable.'
    ),
  /**
   * Candidate ACTIVE CP trades of the given product, newest first. The server
   * (not this call) is what stops a plain client session from ever seeing a row here.
   */
  listCopySources: async (structureVersionId) =>
    payload(
      await get(
        `/api/structured-trades/copy-sources?structureVersionId=${structureVersionId}`
      ),
      'Unable to load existing trades to copy.'
    ),
  /**
   * One candidate's field values, matched against the target version on a best-effort basis,
   * plus the target fields that had no match (unmatchedFields) so the desk can see what did
   * not copy.
   */
  loadCopySourceFields: async (structureVersionId, transactionId) =>
    payload(
      await get(
        `/api/structured-trades/copy-sources/${transactionId}?structureVersionId=${structureVersionId}`
      ),
      'Unable to load the source trade.'
    ),
  submitTrade: async (request) => {
    const response = await post('/api/structured-trades', {
      ...request,
      ...(sourceTransactionId != null ? { sourceTransactionId } : {}),
    });
    const data = payload(response, 'Submission failed. Please try again.');
    const transactionId =
      data.transaction_id ||
      data.transactionId ||
      response.transaction_id ||
      response.transactionId;
    if (!transactionId)
      throw new Error(
        extractErrorMessage(
          response,
          'Submission returned no transaction reference.'
        )
      );
    return {
      ...response,
      ...data,
      transactionId,
      productCode:
        data.productCode || data.product_code || response.productCode,
    };
  },
});

// Backward-compatible default: every existing caller keeps working unchanged, with no copy
// source ever attached to a booking.
export const clientEngineServices = createClientEngineServices({});
